// Pure decision logic for the transport table cells (carrier / driver / vehicle).
//
// Kept separate from `transactionPipelineFields.tsx` so the branching — when a
// blank field prompts "Uzupełnij" vs. shows nothing — is unit-testable without
// rendering. The .tsx maps these specs onto <MultiRowStack> rows.

/** Treat blank/whitespace-only strings as absent (transport fields are
 *  sometimes stored as "" rather than null — e.g. an empty `vehicle_plate`). */
export const blankToNull = (s: string | null | undefined): string | null =>
  s != null && String(s).trim() !== "" ? String(s) : null;

export interface TransportLegInput {
  id: number | string;
  /** The field value for this leg (carrier short name / driver name / plate). */
  value: string | null | undefined;
  /** Optional secondary line (e.g. driver phone). */
  secondary?: string | null;
}

export type TransportRowSpec =
  | { id: string; hint: false; primary: string; secondary: string | null }
  | { id: string; hint: true };

/**
 * What each transport cell row should show:
 *   - a leg carrying the value → the value (+ optional secondary);
 *   - a leg that EXISTS but is missing the value → the "Uzupełnij" hint, always
 *     (even when transport is otherwise complete — a set-up transport can still
 *     lack a driver or vehicle);
 *   - no transport leg at all → the hint ONLY while transport is still pending
 *     (required-but-not-set-up); a not-required/complete transport shows nothing.
 */
export function transportCellRows(
  legs: readonly TransportLegInput[],
  transportPending: boolean,
): TransportRowSpec[] {
  const rows: TransportRowSpec[] = [];
  for (const leg of legs) {
    const value = blankToNull(leg.value);
    if (value) {
      rows.push({ id: String(leg.id), hint: false, primary: value, secondary: blankToNull(leg.secondary) });
    } else {
      rows.push({ id: String(leg.id), hint: true });
    }
  }
  if (rows.length === 0 && transportPending) {
    rows.push({ id: "to-provide", hint: true });
  }
  return rows;
}
